
import { Component, Vue, Watch } from 'vue-property-decorator';
import shopifyFlowService, { FlowEvent, FlowEventQueryParams } from '@/services/shopify-flow.service';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import {
  ATLAS_FEATURE_LINKS,
  getFeatureVersion,
  setFeatureVersion,
} from '@/config/atlasFeatures';

interface Snackbar {
  show: boolean;
  message: string;
  color: string;
}

interface Filters {
  status: string | null;
  eventType: string | null;
}

interface TableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
}

@Component({
  name: 'ShopifyFlowEvents',
  components: {
    CopyLinkButton,
    TryNewAtlasBanner,
  },
})
export default class ShopifyFlowEvents extends Vue {
  private loading = false;
  private loadingDetails = false;
  private retrying: string | null = null;
  private events: FlowEvent[] = [];
  private totalEvents = 0;
  private detailDialog = false;
  private selectedEvent: FlowEvent | null = null;

  private tableOptions: TableOptions = {
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
  };

  private filters: Filters = {
    status: null,
    eventType: null,
  };

  private snackbar: Snackbar = {
    show: false,
    message: '',
    color: 'success',
  };

  private headers = [
    { text: 'Event ID', value: 'eventId', width: '200px' },
    { text: 'Type', value: 'eventType', width: '180px' },
    { text: 'Resource', value: 'resourceType', width: '140px' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Attempts', value: 'attempts', width: '90px', align: 'center' },
    { text: 'Created', value: 'createdAt', width: '140px' },
    { text: 'Actions', value: 'actions', sortable: false, width: '100px' },
  ];

  private statusOptions = [
    { text: 'Pending', value: 'pending' },
    { text: 'Sent', value: 'sent' },
    { text: 'Failed', value: 'failed' },
  ];

  private eventTypeOptions = [
    { text: 'Purchase Order Created', value: 'purchase_order_created' },
  ];

  // Computed properties for stats
  private get sentCount(): number {
    return this.events.filter((e) => e.status === 'sent').length;
  }

  private get pendingCount(): number {
    return this.events.filter((e) => e.status === 'pending').length;
  }

  private get failedCount(): number {
    return this.events.filter((e) => e.status === 'failed').length;
  }

  @Watch('tableOptions', { deep: true, immediate: true })
  onTableOptionsChange(): void {
    this.loadEvents();
  }

  private onFilterChange(): void {
    // Reset to first page when filters change to avoid empty page results
    this.tableOptions.page = 1;
    this.loadEvents();
  }

  private async loadEvents(): Promise<void> {
    try {
      this.loading = true;

      const params: FlowEventQueryParams = {
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
      };

      if (this.filters.status) {
        params.status = this.filters.status as 'pending' | 'sent' | 'failed';
      }

      if (this.filters.eventType) {
        params.eventType = this.filters.eventType;
      }

      const response = await shopifyFlowService.getEvents(params);
      this.events = response.data || [];
      this.totalEvents = response.pagination?.total || 0;
    } catch (error) {
      console.error('Failed to load events:', error);
      this.showSnackbar('Failed to load events', 'error');
    } finally {
      this.loading = false;
    }
  }

  private async retryEvent(event: FlowEvent): Promise<void> {
    try {
      this.retrying = event.eventId;
      await shopifyFlowService.retryEvent(event.eventId);
      this.showSnackbar('Event retry queued successfully', 'success');
      await this.loadEvents();
    } catch (error) {
      console.error('Failed to retry event:', error);
      this.showSnackbar('Failed to retry event', 'error');
    } finally {
      this.retrying = null;
    }
  }

  private async viewDetails(event: FlowEvent): Promise<void> {
    try {
      this.loadingDetails = true;
      // Fetch full event details (list endpoint omits payload/response)
      const fullEvent = await shopifyFlowService.getEventById(event.eventId);
      this.selectedEvent = fullEvent;
      this.detailDialog = true;
    } catch (error) {
      console.error('Failed to load event details:', error);
      this.showSnackbar('Failed to load event details', 'error');
    } finally {
      this.loadingDetails = false;
    }
  }

  private getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending: 'orange',
      sent: 'success',
      failed: 'error',
    };
    return colors[status] || 'grey';
  }

  private getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      pending: 'mdi-clock-outline',
      sent: 'mdi-check',
      failed: 'mdi-alert-circle',
    };
    return icons[status] || 'mdi-help';
  }

  private formatEventType(eventType: string): string {
    if (!eventType) return '';
    return eventType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  private formatJson(obj: Record<string, unknown>): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  private showSnackbar(message: string, color: string): void {
    this.snackbar = {
      show: true,
      message,
      color,
    };
  }

  private get showTryNewShopifyFlowBanner(): boolean {
    return getFeatureVersion('shopifyFlow') === 'classic';
  }

  private switchShopifyFlowToAtlas(): void {
    setFeatureVersion('shopifyFlow', 'atlas');
    this.$router.push(ATLAS_FEATURE_LINKS.shopifyFlow['/shopify-flow/events']);
  }
}
